import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AxiosError } from "axios";
import { MobilePageHeader } from "../components/layout/MobileAppChrome";
import PageSpinner from "../components/shared/PageSpinner";
import { fetchMyServiceRequests } from "../services/serviceRequestService";
import { ServiceRequest, ServiceRequestStatus } from "../types/store";
import { toast } from "../utils/toast";
import "../styles/pages/ServiceTrackingPage.css";

const serviceTimeline: { status: ServiceRequestStatus; label: string; description: string }[] = [
  { status: "REQUESTED", label: "Requested", description: "We received your booking request." },
  { status: "UNDER_REVIEW", label: "Under review", description: "Our team is checking availability and details." },
  { status: "CONFIRMED", label: "Confirmed", description: "Your service request has been confirmed." },
  { status: "SCHEDULED", label: "Scheduled", description: "A technician visit has been scheduled." },
  { status: "IN_PROGRESS", label: "In progress", description: "The service is currently underway." },
  { status: "COMPLETED", label: "Completed", description: "The service request has been completed." },
];

const statusOrder: ServiceRequestStatus[] = [
  "REQUESTED",
  "UNDER_REVIEW",
  "CONFIRMED",
  "SCHEDULED",
  "IN_PROGRESS",
  "COMPLETED",
  "CANCELLED",
];

const formatDate = (value: string) => {
  if (!value) {
    return "Not available";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const formatStatus = (status: ServiceRequestStatus) => status.replaceAll("_", " ");

const ServiceTrackingDetailsPage: React.FC = () => {
  const navigate = useNavigate();
  const { requestId } = useParams<{ requestId: string }>();
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState<ServiceRequest[]>([]);

  useEffect(() => {
    let mounted = true;

    fetchMyServiceRequests()
      .then((items) => {
        if (!mounted) {
          return;
        }
        setRequests(items);
      })
      .catch((error) => {
        if (!mounted) {
          return;
        }
        if (error instanceof AxiosError && error.response?.status === 401) {
          toast.error("Please log in to view your service tracking.");
          navigate("/login", { replace: true, state: { from: "/service-tracking" } });
          return;
        }
        toast.error("Unable to load service tracking right now.");
      })
      .finally(() => {
        if (mounted) {
          setLoading(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, [navigate]);

  const latestRequests = useMemo(() => requests.slice(0, 20), [requests]);
  const selectedRequest = useMemo(
    () => latestRequests.find((request) => String(request.id) === requestId),
    [latestRequests, requestId],
  );

  if (loading) {
    return (
      <section className="shell section page-section service-tracking-page">
        <PageSpinner label="Loading service details..." />
      </section>
    );
  }

  if (!selectedRequest) {
    return (
      <section className="shell section page-section service-tracking-page">
        <MobilePageHeader title="Service Details" />
        <div className="service-tracking-empty">
          <h2>Service booking not found</h2>
          <p>This booking is not available in your service tracking list.</p>
          <Link className="button" to="/service-tracking">
            Back to tracking
          </Link>
        </div>
      </section>
    );
  }

  const currentIndex = statusOrder.indexOf(selectedRequest.status);
  const isCancelled = selectedRequest.status === "CANCELLED";

  return (
    <section className="shell section page-section service-tracking-page service-tracking-page--details">
      <MobilePageHeader title="Service Details" />
      <div className="service-tracking-page__header">
        <div>
          <span className="eyebrow">Booking #{selectedRequest.id}</span>
          <h1>{selectedRequest.serviceName}</h1>
        </div>
        <Link className="button service-tracking-page__book-link" to="/service-tracking">
          Back
        </Link>
      </div>

      <article className="service-tracking-card">
        <div className="service-tracking-card__top">
          <div>
            <span className="service-tracking-card__eyebrow">Submitted on {formatDate(selectedRequest.createdAt)}</span>
            <h2>{selectedRequest.serviceName}</h2>
            <p>{selectedRequest.customerName}</p>
          </div>
          <span className={`service-tracking-card__status service-tracking-card__status--${selectedRequest.status.toLowerCase()}`}>
            {formatStatus(selectedRequest.status)}
          </span>
        </div>

        <div className="service-tracking-card__meta">
          <div>
            <span>Preferred date</span>
            <strong>{selectedRequest.preferredDate}</strong>
          </div>
          <div>
            <span>Time slot</span>
            <strong>{selectedRequest.preferredTimeSlot}</strong>
          </div>
          <div>
            <span>Postal code</span>
            <strong>{selectedRequest.postalCode}</strong>
          </div>
          <div>
            <span>Phone</span>
            <strong>{selectedRequest.phoneNumber}</strong>
          </div>
          <div className="service-tracking-card__meta--wide">
            <span>Address</span>
            <strong>{selectedRequest.address}</strong>
          </div>
          {selectedRequest.description ? (
            <div className="service-tracking-card__meta--wide">
              <span>Details</span>
              <strong>{selectedRequest.description}</strong>
            </div>
          ) : null}
        </div>

        <div className="service-tracking-timeline">
          {serviceTimeline.map((step, index) => {
            const stepIndex = statusOrder.indexOf(step.status);
            const isComplete = !isCancelled && currentIndex >= stepIndex && currentIndex !== -1;
            const isCurrent = !isCancelled && currentIndex === stepIndex;

            return (
              <div
                key={step.status}
                className={`service-tracking-timeline__step ${isComplete ? "is-complete" : ""} ${isCurrent ? "is-current" : ""}`}
              >
                <span className="service-tracking-timeline__dot" aria-hidden="true" />
                {index < serviceTimeline.length - 1 ? <span className="service-tracking-timeline__rail" aria-hidden="true" /> : null}
                <div>
                  <strong>{step.label}</strong>
                  <p>{step.description}</p>
                </div>
              </div>
            );
          })}
        </div>

        {isCancelled ? (
          <div className="service-tracking-card__note service-tracking-card__note--cancelled">
            <strong>Cancelled</strong>
            <p>This service request is currently cancelled. Please contact support if you need help.</p>
          </div>
        ) : null}

        <div className="service-tracking-card__footer">
          <span>Submitted on {formatDate(selectedRequest.createdAt)}</span>
          <span>{selectedRequest.userName ? `Requested by ${selectedRequest.userName}` : "Customer request"}</span>
        </div>
      </article>
    </section>
  );
};

export default ServiceTrackingDetailsPage;
